import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Badge,
  styled,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Stack,
  Divider,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Videocam as VideocamIcon,
  Person as PersonIcon,
  Assignment,
  People,
} from '@mui/icons-material';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, isSameDay, startOfMonth, endOfMonth } from 'date-fns';
import { dashboardAPI, bookingsAPI } from '../services/api';
import { useLanguage } from '../context/LanguageContext';

const GS = {
  blue: '#FF8C42',
  blueLight: '#FFF5EF',
  blueDark: '#E06526',
  orange: '#FF8C42',
  orangeLight: '#FFF5EF',
  orangeDark: '#E06526',
  amber: '#F0A500',
  teal: '#4A7C8E',
  tealLight: '#E6F2F5',
  tealDark: '#1E3A45',
  textPrimary: '#1E3A45',
  textMuted: '#6B7280',
  textLight: '#9CA3AF',
  border: '#E5E7EB',
  surface: '#F9FAFB',
  white: '#ffffff',
};

const getStatusColor = (status) => {
  const s = String(status || '').toUpperCase();
  if (s === 'CONFIRMED' || s === 'APPROVE' || s === 'APPROVED') return { bg: GS.blueLight, color: GS.blueDark };
  if (s === 'PENDING') return { bg: GS.tealLight, color: GS.tealDark };
  if (s === 'COMPLETED' || s === 'COMPLETE') return { bg: '#F0FDF4', color: '#15803D' };
  if (s === 'CANCELLED' || s === 'CANCEL' || s === 'REJECTED' || s === 'REJECT') return { bg: '#FEF2F2', color: '#B91C1C' };
  return { bg: GS.surface, color: GS.textMuted };
};

function extractBookingsList(res) {
  const root = res?.data?.data ?? res?.data;
  if (Array.isArray(root)) return root;
  if (Array.isArray(root?.items)) return root.items;
  if (Array.isArray(root?.bookings)) return root.bookings;
  return [];
}

function mergeBookingViewPayload(p) {
  if (!p || typeof p !== 'object') return p;
  const nested =
    (p.booking && typeof p.booking === 'object' && p.booking) ||
    (p.bookingDetails && typeof p.bookingDetails === 'object' && p.bookingDetails) ||
    null;
  if (!nested) return p;
  return { ...nested, ...p, ...nested };
}

function pickServiceAddress(d) {
  if (!d || typeof d !== 'object') return '';
  const guest = d.guest && typeof d.guest === 'object' ? d.guest : null;
  const raw =
    d.serviceAddress ??
    d.service_address ??
    d.ServiceAddress ??
    d.address ??
    d.serviceLocation ??
    d.service_location ??
    guest?.serviceAddress ??
    guest?.service_address ??
    guest?.address;
  if (raw == null) return '';
  return String(raw).trim();
}

function normalizeBookingRow(b, idx) {
  const start = b.startTime ?? b.start ?? b.slotTime ?? b.appointmentStartTime ?? '';
  const end = b.endTime ?? b.end ?? b.slotEndTime ?? b.appointmentEndTime ?? '';
  const sFormatted = String(start).includes(':') ? String(start).substring(0, 5) : start;
  const eFormatted = String(end).includes(':') ? String(end).substring(0, 5) : end;

  let time = '—';
  if (sFormatted && eFormatted) time = `${sFormatted}–${eFormatted}`;
  else if (sFormatted) time = sFormatted;
  else if (b.time) time = b.time;

  const confCode =
    b.confirmationCode ??
    b.confirmation_code ??
    b.bookingConfirmationCode ??
    b.booking_confirmation_code ??
    b.ConfirmationCode ??
    '—';

  const guestName = b.guest
    ? [b.guest.firstName, b.guest.lastName].filter(Boolean).join(' ')
    : '';
  const customerName =
    [b.customerFirstName, b.customerLastName].filter(Boolean).join(' ') ||
    (b.customerName && String(b.customerName).trim()) ||
    guestName ||
    (b.name && String(b.name).trim()) ||
    '—';

  return {
    id: b.id ?? b.bookingId ?? idx,
    title: b.title ?? b.serviceName ?? b.templateName ?? '—',
    customerName,
    contactName: b.contactPerson || b.providerName || b.contactName || '—',
    status: b.status ?? '—',
    meetingType: b.meetingType || 'Offline',
    meetingUrl:
      b.meetingUrl ??
      b.meetingLink ??
      b.meeting_link ??
      b.url ??
      b.joinUrl ??
      b.join_url ??
      '',
    time,
    confirmationCode: confCode,
    serviceAddress: pickServiceAddress(b),
    raw: b,
  };
}

const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) => prop !== 'hasBooking' && prop !== 'isHighlighted',
})(({ theme, hasBooking, isHighlighted }) => ({
  ...(hasBooking && {
    backgroundColor: GS.blueLight,
    color: GS.blueDark,
    fontWeight: 'bold',
    '&:hover, &:focus': {
      backgroundColor: GS.blue,
      color: theme.palette.common.white,
    },
  }),
  ...(isHighlighted && {
    backgroundColor: GS.blue,
    color: theme.palette.common.white,
    '&:hover, &:focus': {
      backgroundColor: GS.blueDark,
    },
  }),
}));

const APPOINTMENTS_TABLE_VISIBLE_ROWS = 5;
const APPOINTMENTS_TABLE_ROW_HEIGHT = 52;
const APPOINTMENTS_TABLE_HEADER_HEIGHT = 52;
const APPOINTMENTS_TABLE_MAX_HEIGHT =
  APPOINTMENTS_TABLE_HEADER_HEIGHT + APPOINTMENTS_TABLE_ROW_HEIGHT * APPOINTMENTS_TABLE_VISIBLE_ROWS;

const AppointmentsTable = ({ bookings, onView, t }) => {
  const headCellSx = {
    fontWeight: 700,
    color: '#475569',
    py: 0,
    px: 1.5,
    fontSize: '0.8rem',
    height: APPOINTMENTS_TABLE_HEADER_HEIGHT,
    boxSizing: 'border-box',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    verticalAlign: 'middle',
  };
  const bodyCellSx = {
    py: 0,
    px: 1.5,
    fontSize: '0.85rem',
    height: APPOINTMENTS_TABLE_ROW_HEIGHT,
    boxSizing: 'border-box',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    verticalAlign: 'middle',
  };

  return (
    <TableContainer
      sx={{
        border: `1px solid ${GS.border}`,
        borderRadius: 2,
        height: APPOINTMENTS_TABLE_MAX_HEIGHT,
        maxHeight: APPOINTMENTS_TABLE_MAX_HEIGHT,
        overflowX: 'hidden',
        overflowY: 'auto',
        width: '100%',
      }}
    >
      <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
        <TableHead>
          <TableRow sx={{ bgcolor: '#f8fafc', height: APPOINTMENTS_TABLE_HEADER_HEIGHT }}>
            <TableCell sx={headCellSx}>{t('pages.bookings.table.time')}</TableCell>
            <TableCell sx={headCellSx}>{t('pages.bookings.dialog.confirmationCode')}</TableCell>
            <TableCell sx={headCellSx}>{t('pages.bookings.table.customerName')}</TableCell>
            <TableCell sx={headCellSx}>{t('pages.bookings.dialog.labelContactPerson')}</TableCell>
            <TableCell sx={headCellSx}>{t('pages.bookings.table.meetingType')}</TableCell>
            <TableCell sx={headCellSx}>{t('pages.bookings.table.status')}</TableCell>
            <TableCell align="right" sx={headCellSx}>
              {t('pages.bookings.table.actions')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {bookings.map((booking) => {
            const sc = getStatusColor(booking.status);
            const isOnline = String(booking.meetingType || '').toUpperCase() === 'ONLINE';
            const meetingLabel = isOnline
              ? t('pages.bookings.meetingType.online')
              : t('pages.bookings.meetingType.offline');
            const meetingUrl = String(booking.meetingUrl || '').trim();

            return (
              <TableRow
                key={booking.id}
                hover
                sx={{
                  height: APPOINTMENTS_TABLE_ROW_HEIGHT,
                  '&:hover': { bgcolor: 'rgba(255, 140, 66, 0.05) !important' },
                }}
              >
                <TableCell sx={{ ...bodyCellSx, fontWeight: 600, color: '#1e293b' }}>
                  {booking.time}
                </TableCell>
                <TableCell
                  sx={{ ...bodyCellSx, fontFamily: 'monospace', fontWeight: 600, color: '#475569' }}
                >
                  {booking.confirmationCode}
                </TableCell>
                <TableCell sx={{ ...bodyCellSx, fontWeight: 600, color: '#1e293b' }}>{booking.customerName}</TableCell>
                <TableCell sx={{ ...bodyCellSx, color: GS.textMuted }}>{booking.contactName}</TableCell>
                <TableCell sx={bodyCellSx}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {isOnline ? (
                      <Tooltip
                        title={
                          meetingUrl
                            ? t('pages.bookings.table.joinOnlineMeeting')
                            : t('pages.bookings.table.noMeetingLink')
                        }
                        arrow
                      >
                        <span>
                          <IconButton
                            size="small"
                            disabled={!meetingUrl}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (meetingUrl) window.open(meetingUrl, '_blank', 'noopener,noreferrer');
                            }}
                            sx={{ p: 0.25, color: GS.blue, '&:hover': { bgcolor: GS.blueLight } }}
                          >
                            <VideocamIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : (
                      <PersonIcon sx={{ fontSize: 18, color: GS.teal, ml: 0.25 }} />
                    )}
                    <Typography variant="body2" noWrap sx={{ fontSize: '0.85rem', minWidth: 0 }}>
                      {meetingLabel}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell sx={bodyCellSx}>
                  <Chip
                    size="small"
                    label={booking.status || '—'}
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.7rem',
                      bgcolor: sc.bg,
                      color: sc.color,
                      textTransform: 'capitalize',
                      borderRadius: '6px',
                    }}
                  />
                </TableCell>
                <TableCell align="right" sx={bodyCellSx}>
                  <Tooltip title={t('common.view') || 'View'}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onView(booking);
                      }}
                      sx={{ color: GS.textLight, '&:hover': { bgcolor: GS.surface, color: GS.blue } }}
                    >
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const DashboardCalendar = () => {
  const { t } = useLanguage();
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [currentMonth, setCurrentMonth] = useState(() => new Date());
  const [bookingsByDate, setBookingsByDate] = useState({});
  const [loading, setLoading] = useState(false);
  const [dayBookings, setDayBookings] = useState([]);
  const [dayLoading, setDayLoading] = useState(false);

  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewBookingData, setViewBookingData] = useState(null);

  const detailLabelSx = {
    fontSize: '0.72rem',
    fontWeight: 700,
    color: '#718096',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    mb: 0.6,
    display: 'block',
  };

  const detailValueSx = {
    fontSize: '0.95rem',
    fontWeight: 500,
    color: '#1a202c',
    lineHeight: 1.5,
    wordBreak: 'break-word',
  };

  const formatAppointmentDateOnly = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };

  const formatTimeField = (value) => {
    if (value === undefined || value === null || String(value).trim() === '') return '—';
    const s = String(value).trim();
    if (/^\d{1,2}:\d{2}:\d{2}/.test(s)) return s.slice(0, 5);
    return s;
  };

  const handleViewBooking = async (booking) => {
    const bookingId = booking?.id || booking?.bookingId;
    if (!bookingId) return;
    setViewDialogOpen(true);
    setViewLoading(true);
    setViewBookingData(null);
    try {
      const response = await bookingsAPI.getBookingPopup(bookingId);
      const payload =
        response?.data?.data?.data ||
        response?.data?.data ||
        response?.data?.payload ||
        response?.data ||
        null;
      const row = booking?.raw && typeof booking.raw === 'object' ? booking.raw : booking;
      const merged =
        payload && typeof payload === 'object'
          ? mergeBookingViewPayload({ ...row, ...payload })
          : mergeBookingViewPayload(row);
      setViewBookingData(merged);
    } catch (error) {
      console.error('Failed to load booking details', error);
    } finally {
      setViewLoading(false);
    }
  };

  useEffect(() => {
    const fetchMonthlyBookings = async () => {
      setLoading(true);
      try {
        const start = startOfMonth(currentMonth);
        const end = endOfMonth(currentMonth);
        const startISO = start.toISOString();
        const endISO = end.toISOString();

        const res = await dashboardAPI.getSlotsByDay({ startDate: startISO, endDate: endISO });
        const slotsRows = res?.data?.data || res?.data || [];

        const counts = {};
        if (Array.isArray(slotsRows)) {
          slotsRows.forEach((s) => {
            const ymd = s.date ? s.date.substring(0, 10) : (s.day ? s.day.substring(0, 10) : null);
            if (ymd) {
              counts[ymd] = Number(s.bookingsCount ?? s.bookings ?? s.bookingCount ?? 0);
            }
          });
        }
        setBookingsByDate(counts);
      } catch (error) {
        console.error('Error fetching monthly bookings', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMonthlyBookings();
  }, [currentMonth]);

  useEffect(() => {
    const fetchDayBookings = async () => {
      setDayLoading(true);
      try {
        const slotDate = format(selectedDate, 'yyyy-MM-dd');
        const res = await bookingsAPI.getBookings({ slotDate }, 0, 100);
        const items = extractBookingsList(res);
        setDayBookings(items.map(normalizeBookingRow));
      } catch (error) {
        console.error('Error fetching day bookings', error);
        setDayBookings([]);
      } finally {
        setDayLoading(false);
      }
    };
    fetchDayBookings();
  }, [selectedDate]);

  const ServerDay = (props) => {
    const { day, outsideCurrentMonth, ...other } = props;
    const dateStr = format(day, 'yyyy-MM-dd');
    const hasBooking = bookingsByDate[dateStr] > 0;
    const isSelected = isSameDay(day, selectedDate);

    return (
      <Badge key={day.toString()} overlap="circular" badgeContent={hasBooking ? undefined : undefined}>
        <CustomPickersDay
          {...other}
          outsideCurrentMonth={outsideCurrentMonth}
          day={day}
          hasBooking={hasBooking && !isSelected}
          isHighlighted={isSelected}
        />
      </Badge>
    );
  };

  const handleMonthChange = (newMonth) => {
    setCurrentMonth(newMonth);
  };

  return (
    <>
      <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${GS.border}`, bgcolor: GS.white, overflow: 'hidden' }}>
        <Box
          sx={{
            p: { xs: 2, sm: 3 },
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 4,
            overflow: 'hidden',
            minWidth: 0,
          }}
        >
          <Box sx={{ flexShrink: 0, width: { xs: '100%', md: 350 }, maxWidth: '100%', p: 2, borderRadius: 3, bgcolor: GS.surface, border: `1px solid ${GS.border}` }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateCalendar
                value={selectedDate}
                onChange={(newValue) => newValue && setSelectedDate(newValue)}
                onMonthChange={handleMonthChange}
                loading={loading}
                renderLoading={() => (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <CircularProgress size={24} sx={{ color: GS.blue }} />
                  </Box>
                )}
                slots={{ day: ServerDay }}
                sx={{
                  width: '100%',
                  maxWidth: '100%',
                  '& .MuiDateCalendar-root': { width: '100%', maxWidth: '100%' },
                }}
              />
            </LocalizationProvider>
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexShrink: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, color: GS.textPrimary }}>
                {t('pages.bookings.appointments')}{' '}
                <Typography component="span" variant="h6" sx={{ color: GS.textMuted, fontWeight: 500 }}>
                  ({dayBookings.length})
                </Typography>
              </Typography>
            </Box>

            {dayLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} sx={{ color: GS.blue }} />
              </Box>
            ) : dayBookings.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6, color: GS.textMuted }}>
                <Typography variant="body1">No appointments for this date.</Typography>
              </Box>
            ) : (
              <AppointmentsTable bookings={dayBookings} onView={handleViewBooking} t={t} />
            )}
          </Box>
        </Box>
      </Paper>

      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          elevation: 8,
          sx: { borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider' },
        }}
      >
        <DialogTitle
          sx={{
            px: 3,
            py: 2.5,
            background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {t('pages.bookings.dialog.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('pages.bookings.dialog.subtitle')}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 0, py: 0, bgcolor: '#f4f6f9' }}>
          {viewLoading ? (
            <Box sx={{ py: 6, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={28} sx={{ color: GS.blue }} />
              <Typography variant="body2" color="text.secondary">
                {t('pages.bookings.dialog.loading')}
              </Typography>
            </Box>
          ) : viewBookingData ? (
            <Stack spacing={0} divider={<Divider flexItem sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />}>
              <Box sx={{ px: 3, py: 2.5, bgcolor: '#fff' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box sx={{ p: 0.8, borderRadius: 1.5, bgcolor: GS.blueLight, color: GS.blue }}>
                    <Assignment sx={{ fontSize: 20, display: 'block' }} />
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a202c' }}>
                    {t('pages.bookings.dialog.sectionService')}
                  </Typography>
                </Box>
                <Grid container spacing={3} sx={{ mt: 0.5 }}>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={detailLabelSx}>{t('pages.bookings.dialog.labelTitle')}</Typography>
                    <Typography sx={detailValueSx}>{viewBookingData.title || '—'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={detailLabelSx}>Meeting Type</Typography>
                    <Typography sx={{ ...detailValueSx, textTransform: 'capitalize' }}>{viewBookingData.meetingType || 'Offline'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={detailLabelSx}>{t('pages.bookings.dialog.labelContactPerson')}</Typography>
                    <Typography sx={detailValueSx}>
                      {viewBookingData.contactPerson || viewBookingData.providerName || '—'}
                    </Typography>
                  </Grid>
                  {viewBookingData.meetingUrl && (
                    <Grid item xs={12} sm={6}>
                      <Typography sx={detailLabelSx}>Meeting Link</Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Button
                          variant="contained"
                          size="small"
                          href={viewBookingData.meetingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            textTransform: 'none',
                            borderRadius: 2,
                            bgcolor: GS.blue,
                            color: '#fff',
                            fontWeight: 700,
                            px: 2,
                            py: 0.5,
                            fontSize: '0.8rem',
                            '&:hover': { bgcolor: GS.blueDark },
                          }}
                        >
                          Join Online Meeting
                        </Button>
                      </Box>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Typography sx={detailLabelSx}>{t('pages.bookings.dialog.labelAddress')}</Typography>
                    <Typography sx={detailValueSx}>{pickServiceAddress(viewBookingData) || '—'}</Typography>
                  </Grid>
                </Grid>
              </Box>

              <Box sx={{ px: 3, py: 2.5, bgcolor: '#fafbfc' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box sx={{ p: 0.8, borderRadius: 1.5, bgcolor: GS.tealLight, color: GS.teal }}>
                    <People sx={{ fontSize: 20, display: 'block' }} />
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a202c' }}>
                    {t('pages.bookings.dialog.sectionCustomer')}
                  </Typography>
                </Box>
                <Grid container spacing={3} sx={{ mt: 0.5 }}>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={detailLabelSx}>{t('pages.bookings.dialog.labelName')}</Typography>
                    <Typography sx={detailValueSx}>
                      {[viewBookingData.customerFirstName, viewBookingData.customerLastName].filter(Boolean).join(' ') ||
                        viewBookingData.customerName ||
                        viewBookingData.name ||
                        '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={detailLabelSx}>{t('pages.bookings.dialog.labelEmail')}</Typography>
                    <Typography sx={detailValueSx}>{viewBookingData.customerEmail || viewBookingData.email || '—'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={detailLabelSx}>{t('pages.bookings.dialog.labelPhone')}</Typography>
                    <Typography sx={detailValueSx}>
                      {viewBookingData.customerPhone || viewBookingData.phone || viewBookingData.phoneNumber || '—'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Box sx={{ px: 3, py: 2.5, bgcolor: '#fff' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Box sx={{ p: 0.8, borderRadius: 1.5, bgcolor: GS.orangeLight, color: GS.orange }}>
                    <Assignment sx={{ fontSize: 20, display: 'block' }} />
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a202c' }}>
                    {t('pages.bookings.dialog.schedule')}
                  </Typography>
                </Box>
                <Grid container spacing={3} sx={{ mt: 0.5 }}>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={detailLabelSx}>{t('pages.bookings.dialog.confirmationCode')}</Typography>
                    <Typography
                      sx={{
                        ...detailValueSx,
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                        letterSpacing: '0.06em',
                      }}
                    >
                      {viewBookingData.confirmationCode ||
                        viewBookingData.confirmation_code ||
                        viewBookingData.bookingConfirmationCode ||
                        viewBookingData.booking_confirmation_code ||
                        viewBookingData.ConfirmationCode ||
                        '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={detailLabelSx}>{t('pages.bookings.dialog.appointmentDate')}</Typography>
                    <Typography sx={detailValueSx}>
                      {formatAppointmentDateOnly(viewBookingData.appointmentDate || viewBookingData.date)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={detailLabelSx}>{t('pages.bookings.dialog.startTime')}</Typography>
                    <Typography sx={detailValueSx}>
                      {formatTimeField(viewBookingData.appointmentStartTime || viewBookingData.startTime)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={detailLabelSx}>{t('pages.bookings.dialog.endTime')}</Typography>
                    <Typography sx={detailValueSx}>
                      {formatTimeField(viewBookingData.appointmentEndTime || viewBookingData.endTime)}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Stack>
          ) : (
            <Box sx={{ px: 3, py: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">{t('pages.bookings.dialog.noDetails')}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, bgcolor: '#fff', borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            variant="contained"
            onClick={() => setViewDialogOpen(false)}
            sx={{ textTransform: 'none', fontWeight: 600, px: 3, borderRadius: 2, bgcolor: GS.blue, '&:hover': { bgcolor: GS.blueDark } }}
          >
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DashboardCalendar;
